import { ComponentRef, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  ErrorSummary,
  Field,
  Input,
  Label,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { CheckmarkIcon } from '@navikt/aksel-icons';
import { useChangePasswordMutation } from '../../../hooks/mutations/useChangePasswordMutation';
import { PASSWORD_RULES, PasswordField, PasswordForm as Form, passwordErrors } from '../passwordRules';
import { SettingsSection } from '../../../components/TeamSettingsLayout';
import classes from '../SettingsPage.module.css';

const FIELDS: { field: PasswordField; id: string; autoComplete: string }[] = [
  { field: 'current', id: 'password-current', autoComplete: 'current-password' },
  { field: 'next', id: 'password-new', autoComplete: 'new-password' },
  { field: 'repeat', id: 'password-repeat', autoComplete: 'new-password' },
];

const EMPTY: Form = { current: '', next: '', repeat: '' };

/** Bytt passord, med kravene synlige mens man skriver */
export const PasswordForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>(EMPTY);
  const [show, setShow] = useState(false);
  const [tried, setTried] = useState(false);
  const [saved, setSaved] = useState(false);
  const [wrongCurrent, setWrongCurrent] = useState(false);
  const [summaryTick, setSummaryTick] = useState(0);
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const mutation = useChangePasswordMutation();

  const errors = [
    ...(wrongCurrent ? [{ field: 'current' as const, message: 'settings.password.errors.wrongCurrent' }] : []),
    ...(tried ? passwordErrors(form) : []),
  ];
  const errorFor = (field: PasswordField) => errors.find((error) => error.field === field);

  useEffect(() => {
    if (summaryTick > 0) summaryRef.current?.focus();
  }, [summaryTick]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    setTried(true);
    setWrongCurrent(false);
    if (passwordErrors(form).length > 0) {
      setSummaryTick((tick) => tick + 1);
      return;
    }
    mutation.mutate(
      { oldPassword: form.current, newPassword: form.next },
      {
        onSuccess: () => {
          setForm(EMPTY);
          setTried(false);
          setSaved(true);
        },
        onError: (error) => {
          // Backend svarer med denne koden når det nåværende passordet er feil
          const { response } = error as { response?: { data?: { errorCode?: string } } };
          if (response?.data?.errorCode === 'INVALID_CURRENT_PASSWORD') {
            setWrongCurrent(true);
            setSummaryTick((tick) => tick + 1);
          }
        },
      },
    );
  };

  return (
    <SettingsSection headingId="settings-password" title={t('settings.password.title')} description={t('settings.password.description')}>
      <form className={classes.form} onSubmit={submit} noValidate aria-labelledby="settings-password">
      {saved && (
        <Alert data-color="success" role="status">
          <Paragraph>{t('settings.password.saved')}</Paragraph>
        </Alert>
      )}
      {mutation.isError && !wrongCurrent && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{t('settings.password.saveFailed')}</Paragraph>
        </Alert>
      )}
      {errors.length > 0 && (
        <ErrorSummary ref={summaryRef}>
          <ErrorSummary.Heading>{t('settings.errorSummary')}</ErrorSummary.Heading>
          <ErrorSummary.List>
            {errors.map((error) => {
              const id = FIELDS.find((field) => field.field === error.field)!.id;
              return (
                <ErrorSummary.Item key={error.field}>
                  <ErrorSummary.Link
                    href={`#${id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      document.getElementById(id)?.focus();
                    }}
                  >
                    {t(error.message)}
                  </ErrorSummary.Link>
                </ErrorSummary.Item>
              );
            })}
          </ErrorSummary.List>
        </ErrorSummary>
      )}

      {FIELDS.map(({ field, id, autoComplete }) => {
        const error = errorFor(field);
        const describedBy = [field === 'next' ? 'password-rules' : '', error ? `${id}-error` : ''].filter(Boolean).join(' ');
        return (
          <Field key={field} className={classes.field}>
            <Label htmlFor={id}>{t(`settings.password.fields.${field}`)}</Label>
            {field === 'next' && (
              <div id="password-rules" className={classes.rules}>
                <Paragraph data-size="sm" className={classes.subtle}>
                  {t('settings.password.rulesIntro')}
                </Paragraph>
                <ul>
                  {PASSWORD_RULES.map(({ rule, test }) => {
                    const met = test(form.next);
                    return (
                      <li key={rule} data-met={met}>
                        <span aria-hidden="true" className={classes.ruleMark}>
                          {met && <CheckmarkIcon />}
                        </span>
                        {t(`settings.password.rules.${rule}`)}
                        <span className="ds-sr-only">
                          {' '}
                          {t(met ? 'settings.password.ruleMet' : 'settings.password.ruleNotMet')}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <Input
              id={id}
              type={show ? 'text' : 'password'}
              autoComplete={autoComplete}
              value={form[field]}
              aria-invalid={!!error}
              aria-describedby={describedBy || undefined}
              onChange={(event) => {
                setForm((current) => ({ ...current, [field]: event.target.value }));
                setSaved(false);
                if (field === 'current') setWrongCurrent(false);
                // Den gamle feilen gjelder ikke lenger når brukeren retter
                if (mutation.isError) mutation.reset();
              }}
            />
            {error && <ValidationMessage id={`${id}-error`}>{t(error.message)}</ValidationMessage>}
          </Field>
        );
      })}

      <Checkbox
        id="password-show"
        label={t('settings.password.show')}
        className={classes.showPassword}
        checked={show}
        onChange={(event) => setShow(event.target.checked)}
      />

      <div className={classes.cardFooter}>
        <Button type="submit" loading={mutation.isPending}>
          {t('settings.password.submit')}
        </Button>
      </div>
      </form>
    </SettingsSection>
  );
};
